import type { CitationScope } from "./citation-syntax";
import type { SlidesContent } from "../files/slides-content";
import type { AdStudioTemplateImportSource } from "../shared";
import type {
  CopyExample,
  CopyExamplesChannel,
  CopyExamplesContent,
} from "@hightouch/core/content/lcm-copy-examples/types";
import type { StyleDimension } from "../ad-studio/dimension-schema";
import type { LegacyStyleScope } from "./legacy-style-content";
import type { TemplateManifest } from "../../ad-studio/templates/types";
import type { ReviewTargetOperation } from "./review-targets/schema";

// Filename of the context index file injected into the input directory
export const CONTEXT_INDEX_FILENAME = "context-index.json";

export enum AgenticSettingsEntityType {
  AdTemplate = "ad_template",
  ContextMarkdown = "context_markdown",
  ContextFile = "context_file",
  AmpFileMarkdown = "amp_file_markdown",
  AmpFileUpload = "amp_file_upload",
  AmpFileHtml = "amp_file_html",
  AmpFileSlides = "amp_file_slides",
  CopyReferences = "copy_references",
  // An Ad Studio / lifecycle-marketing visual style: a markdown document (same
  // content as context_markdown) whose reference images are cited context_file
  // entities, with agent scopes and test renders in metadata. Rows written
  // before the markdown cutover still hold the legacy json blob on disk; the
  // row mapper renders those to markdown on read (legacy-style-content.ts).
  Style = "style",
  // A fact the agent should remember ("fiscal year starts in July"), either
  // assumed by an agent or verified by a user (the version's status). Short markdown
  // with a title, injected into the system prompt rather than staged as a
  // file, and optionally scoped to parent models / data sources through
  // agentic_settings_entity_scopes. Labeled "Memory" in the UI.
  Definition = "definition",
}

export enum AgenticSettingsEntityOperation {
  Created = "created",
  Updated = "updated",
  Deleted = "deleted",
  Restored = "restored",
}

export enum ContextFileType {
  BrandGuidelines = "brand_guidelines",
  BusinessContext = "business_context",
  // A repeatable practice the agent should recognize and apply (photorealism,
  // rendering phones, ...). Stored like any other context entity
  // (type = context_markdown / context_file) and, in this first iteration,
  // loaded on-demand the same way — the metadata.contextType tag is what marks
  // it a skill and routes it to its own Context Hub surface.
  Skill = "skill",
  // A reference-only context_file cited from a style document. Tagged so the
  // docs surfaces (table, link-existing picker, references list) can hide it;
  // it reaches an agent only through the style that cites it.
  StyleImage = "style_image",
  // The workspace's Launches winner criteria: how the scheduled launches report
  // judges new ads, concepts and campaigns. One per workspace, edited from the
  // Launches page; other ads agents read it like any on-demand context file.
  WinnerCriteria = "winner_criteria",
  // How a workspace launches ads on one platform: naming, statuses, settings, and copy guidance, which the ad
  // launch skills read.
  LaunchPlaybook = "launch_playbook",
}

/** The only two entity types that carry a contextType, so the only ones a skill can be. */
export const SKILL_ENTITY_TYPES = [
  AgenticSettingsEntityType.ContextMarkdown,
  AgenticSettingsEntityType.ContextFile,
] as const;

// Shape of `proposed_entity_key`: the stable identity of a proposal that has no row
// yet. Lives here so every operation schema can share it without importing the schema
// barrel, which imports them.
export const PROPOSED_ENTITY_KEY_PATTERN = /^[a-z0-9][a-z0-9-]{0,63}$/;

// Which agents a context file is routed to: Ad Studio, Lifecycle Studio, and
// the data/reporting agents.
export const AGENT_SCOPES = ["ads", "lcm", "data"] as const;
export type AgentScope = (typeof AGENT_SCOPES)[number];

export const AGENT_SCOPE_LABELS: Record<AgentScope, string> = {
  ads: "ads",
  lcm: "lifecycle marketing (email, SMS, push)",
  data: "data analysis",
};

// Named values so callers reference AGENT_SCOPE.ads instead of a bare "ads".
export const AGENT_SCOPE = {
  ads: "ads",
  lcm: "lcm",
  data: "data",
} as const satisfies Record<AgentScope, AgentScope>;

// Versioned descriptive bag (agentic_settings_entity_versions.metadata),
// carried forward on each version write so classification survives edits.
export type AgenticSettingsEntityMetadata = {
  legacyVersion?: number;
  legacyArtifactId?: string;
  legacyPlansHash?: string;
  contextType?: ContextFileType;
  // Agents this file is visible to. null/absent = applies to ALL agents; a
  // non-null array restricts visibility to the listed scopes. An empty array is
  // coerced to null on write (and defensively on read) so it never hides the
  // file from every agent.
  agentScopes?: AgentScope[] | null;
};

export type AgenticSettingsMarkdownContent = {
  markdown: string;
};

export type AgenticSettingsContextMarkdownContent =
  AgenticSettingsMarkdownContent & {
    // A DAG skill keeps its steps here and its generated text in `markdown`.
    // Opaque outside lib/dag-skills, which parses it.
    dag?: unknown;
    // Derived PDF documents only: null means extraction failed without a reliable missing-image count.
    pdfImagesUnavailable?: number | null;
  };

export type AgenticSettingsDefinitionContent =
  AgenticSettingsMarkdownContent & {
    /**
     * The user themselves supplied what this definition records, rather than an
     * agent inferring it from the schema or the data. The agent sets it when it
     * proposes the assumption; absent means agent-proposed, which is what every
     * definition written before the field carried.
     */
    statedByUser?: boolean;
    /**
     * How the agent arrived at the assumption: its evidence and why it chose it.
     * Stored for later use; nothing reads it yet.
     */
    reasoning?: string;
    /**
     * The assumed version a verified definition version is directly based on.
     * Only persisted on the first verified version; a later edit does not carry it forward.
     */
    basedOnVersionId?: string;
  };

// Narrow union for the free-text `agentic_settings_entity_versions.status` column; only definitions carry one.
export const AGENTIC_SETTINGS_DEFINITION_STATUSES = [
  "assumed",
  "verified",
] as const;
export type AgenticSettingsDefinitionStatus =
  (typeof AGENTIC_SETTINGS_DEFINITION_STATUSES)[number];

// `assets` maps each `ad:<filename>` ref in the markup to its S3 key, like an ad's component_assets.
export type AgenticSettingsHtmlContent = {
  html: string;
  assets: Record<string, string>;
};

export type AgenticSettingsFileContent = {
  filename: string;
  filetype: string;
  filesize: number;
  s3_key: string;
  extracted_text_s3_key?: string | null;
};

/**
 * Kept in this types-only module so frontend imports of entity types do not
 * load ad-template-schema (zod + validateTemplate + ad-persistence).
 */
export type AgenticSettingsAdTemplateContent =
  | {
      status: "importing" | "needs-attention" | "failed";
      importSource: AdStudioTemplateImportSource;
      authoringSessionId: string;
    }
  | {
      status?: "draft" | "ready";
      schemaVersion: 1;
      importSource: "figma" | "html" | "image";
      usageGuidance: string;
      html: string;
      /** Playwright-normalized markup for the structured HTML editor. */
      editorHtml?: string;
      manifest: TemplateManifest;
      assets: Record<string, string>;
      /** Absent only on pre-sample template versions written before image fidelity gating. */
      originalSampleValues?: Record<string, string>;
      preview: {
        url: string;
        width: number;
        height: number;
      };
      authoringSessionId: string;
    };

export type AgenticSettingsCompleteAdTemplateContent = Extract<
  AgenticSettingsAdTemplateContent,
  { schemaVersion: 1 }
>;

export function isCompleteAdTemplateContent(
  content: AgenticSettingsAdTemplateContent,
): content is AgenticSettingsCompleteAdTemplateContent {
  return content.status === "draft" || isReadyAdTemplateContent(content);
}

export function isReadyAdTemplateContent(
  content: AgenticSettingsAdTemplateContent,
): content is AgenticSettingsCompleteAdTemplateContent & { status?: "ready" } {
  // Legacy ready templates have no stored status.
  return content.status === "ready" || content.status === undefined;
}

export type {
  CopyExample,
  CopyExamplesChannel,
  CopyExamplesContent as AgenticSettingsCopyExamplesContent,
};

// The pre-markdown style content: scope, guidance, and dimensions as one json
// blob. Nothing writes it anymore; rows that still hold it are
// rendered to markdown by the row mapper on read, and the migration script
// rewrites them. Only legacy-style-content.ts parses it.
export type AgenticSettingsLegacyStyleContent = {
  scope: LegacyStyleScope;
  guidance: string | null;
  dimensions: StyleDimension[];
};

export type AgenticSettingsEntity = {
  id: string;
  workspaceId: number;
  createdAt: Date;
  createdBy: number | null;
};

type AgenticSettingsEntityVersionBase = {
  /** Workspace unless populated by the managed-entity loader. */
  citationScope?: CitationScope;
  id: string;
  workspaceId: number;
  entityId: string;
  operation: AgenticSettingsEntityOperation;
  name: string;
  description: string | null;
  isActive: boolean;
  /**
   * Hidden from listings; reaches an agent only through documents that cite
   * it. Versioned like content: every version records the flag, carried
   * forward on writes unless deliberately changed.
   */
  isReferenceOnly: boolean;
  createdAt: Date;
  createdBy: number | null;
  createdByInteractionId: string | null;
  metadata: AgenticSettingsEntityMetadata | null;
};

export type AgenticSettingsEntityVersion =
  | (AgenticSettingsEntityVersionBase & {
      type: AgenticSettingsEntityType.AmpFileSlides;
      content: SlidesContent;
    })
  | (AgenticSettingsEntityVersionBase & {
      type: AgenticSettingsEntityType.AmpFileMarkdown;
      content: AgenticSettingsMarkdownContent;
    })
  | (AgenticSettingsEntityVersionBase & {
      type: AgenticSettingsEntityType.AmpFileUpload;
      content: AgenticSettingsFileContent;
    })
  | (AgenticSettingsEntityVersionBase & {
      type: AgenticSettingsEntityType.AmpFileHtml;
      content: AgenticSettingsHtmlContent;
    })
  | (AgenticSettingsEntityVersionBase & {
      type: AgenticSettingsEntityType.AdTemplate;
      content: AgenticSettingsAdTemplateContent;
    })
  | (AgenticSettingsEntityVersionBase & {
      type: AgenticSettingsEntityType.ContextMarkdown;
      content: AgenticSettingsContextMarkdownContent;
    })
  | (AgenticSettingsEntityVersionBase & {
      type: AgenticSettingsEntityType.ContextFile;
      content: AgenticSettingsFileContent;
    })
  | (AgenticSettingsEntityVersionBase & {
      type: AgenticSettingsEntityType.CopyReferences;
      content: CopyExamplesContent;
    })
  | (AgenticSettingsEntityVersionBase & {
      type: AgenticSettingsEntityType.Style;
      content: AgenticSettingsMarkdownContent;
    })
  | (AgenticSettingsEntityVersionBase & {
      type: AgenticSettingsEntityType.Definition;
      content: AgenticSettingsDefinitionContent;
      status: AgenticSettingsDefinitionStatus;
    });

export type AgenticSettingsDefinitionVersion = Extract<
  AgenticSettingsEntityVersion,
  { type: AgenticSettingsEntityType.Definition }
>;

// Generic so that filtering versions carrying extra fields, such as entityCreatedAt,
// still narrows.
export function isAgenticSettingsDefinitionVersion<
  T extends AgenticSettingsEntityVersion,
>(version: T): version is T & AgenticSettingsDefinitionVersion {
  return version.type === AgenticSettingsEntityType.Definition;
}

// Entity versions whose content can be summarized into a description (see
// context-description/generate.ts). copy_references carries no summarizable text.
export type SummarizableEntityVersion = Extract<
  AgenticSettingsEntityVersion,
  {
    type:
      | AgenticSettingsEntityType.ContextMarkdown
      | AgenticSettingsEntityType.ContextFile;
  }
>;

// Single source of truth for the enqueue gate (resolver) and the extraction
// routing (worker). As a type guard, it also narrows the content union, so the
// worker gets per-type content typing without a second switch.
export function isSummarizableEntityVersion(
  version: AgenticSettingsEntityVersion,
): version is SummarizableEntityVersion {
  return (
    version.type === AgenticSettingsEntityType.ContextMarkdown ||
    version.type === AgenticSettingsEntityType.ContextFile
  );
}

/** Whether the version is a skill written as steps, with a `dag`. */
export function isDagSkillVersion(
  version: AgenticSettingsEntityVersion,
): version is Extract<
  AgenticSettingsEntityVersion,
  { type: AgenticSettingsEntityType.ContextMarkdown }
> {
  return (
    version.type === AgenticSettingsEntityType.ContextMarkdown &&
    version.content.dag != null
  );
}

export enum AgenticSettingsReviewOpType {
  DeleteEntity = "delete_entity",
  EditEntity = "edit_entity",
  // Both context_markdown and context_file can be added via a review. The op_type stays
  // generic; the `operation` payload's `entity_type` discriminator carries which one.
  // A markdown add carries inline content; a file add carries a `file_id` reference to a
  // file the user uploaded in the originating interaction (the durable bytes are promoted
  // at apply time — never round-tripped through the agent's output).
  AddEntity = "add_entity",
  // Full-column write against brand_profiles (colors / font families / font
  // files / logos). Not an entity op: entity_id and reviewed_entity_version_id
  // stay null; staleness uses reviewed_updated_at against the profile row.
  UpdateBrandProfile = "update_brand_profile",
  // Inserts a brand_profiles row. Like the update op it is not an entity op, and
  // it carries no staleness token: a create addresses no existing row, so the only
  // conflict it can lose is a name collision at apply time.
  CreateBrandProfile = "create_brand_profile",
  // Sets brand_profiles.image_guardrails.allowImageGeneration. Not an entity op, and no
  // staleness token: it writes one boolean, so there is nothing unseen to overwrite.
  UpdateBrandImageGuardrails = "update_brand_image_guardrails",
  // Merges flag and custom-rule deltas onto amp_asset_groups.image_guardrails at apply
  // time. Deltas rather than a full value, so it needs no staleness token either.
  UpdateCollectionImageGuardrails = "update_collection_image_guardrails",
  // A change to an approved definition. Applying it writes a new verified version, plus the
  // proposed scope when it has one.
  UpdateDefinition = "update_definition",
  CreateChannel = "create_channel",
  UpdateChannel = "update_channel",
  SetDefaultChannelSize = "set_default_channel_size",
  DeleteChannelSize = "delete_channel_size",
  // Approves an unapproved assumption, as it stands or with edits. Stored with every field
  // filled in from the served version, so the proposal is complete. Applies like an update.
  ApproveAssumption = "approve_assumption",
  // Writes a Deleted version of a definition or an assumption.
  DeleteDefinition = "delete_definition",
  // A new definition, approved once accepted. Keyed by its proposed_entity_key.
  CreateDefinition = "create_definition",
  // One approval optionally creates an amp_asset_groups row and imports the selected chat uploads.
  AddAssets = "add_assets",
  ManageLibraryAsset = "manage_library_asset",
}

export enum AgenticSettingsReviewSuggestionReason {
  // Disagrees with another authored thing (doc, style, system prompt).
  Contradiction = "contradiction",
  Redundancy = "redundancy",
  Clarity = "clarity",
  Gap = "gap",
  // Out of sync with the current state of the workspace or the world — deleted
  // brand-profile references, past dates, facts contradicted by live data. Distinct from
  // Contradiction: the other side isn't an authored doc, it's reality.
  Stale = "stale",
}

export enum AgenticSettingsReviewSuggestionStatus {
  Pending = "pending",
  Rejected = "rejected",
  Accepted = "accepted",
}

// The op-specific payload stored in agentic_settings_entity_review_suggestions.operation:
// what the review targets' stored schemas (review-targets/*/schema.ts) parse.
export type AgenticSettingsReviewOperation = ReviewTargetOperation;

// Shared by the doc add and edit operations. context_type and agent_scopes land in
// the entity's metadata bag, brand_profile_ids in its assignment rows.
export type AgenticSettingsReviewDocScoping = {
  context_type?: ContextFileType | null;
  agent_scopes?: AgentScope[] | null;
  brand_profile_ids?: string[] | null;
};

export type AgenticSettingsEntityReview = {
  id: string;
  workspaceId: number;
  llmChatInteractionId: string;
  summary: string;
  createdBy: number | null;
  createdAt: Date;
  updatedAt: Date;
};

export type AgenticSettingsEntityReviewSuggestion = {
  cardSummary: string | null;
  id: string;
  workspaceId: number;
  reviewId: string;
  opType: AgenticSettingsReviewOpType;
  operation: AgenticSettingsReviewOperation;
  reviewedEntityId: string | null;
  reviewedEntityVersionId: string | null;
  appliedToEntityVersionId: string | null;
  summary: string;
  reason: AgenticSettingsReviewSuggestionReason;
  status: AgenticSettingsReviewSuggestionStatus;
  // Provenance. The session is the scope supersession is computed in: the head per
  // (session, reviewedEntityId | proposedEntityKey) is the live proposal.
  createdBySessionId: string | null;
  createdByInteractionId: string | null;
  // add_entity only: the revision chain this proposal belongs to, authored by the agent as
  // a slug it can reproduce next turn. Null for ops that target a live entity, which key
  // by reviewedEntityId instead. Retires at approval.
  proposedEntityKey: string | null;
  // When the suggestion left pending, for the approved card's "Live in ... " timestamp.
  // Null while pending; the check constraint keeps the two in step.
  resolvedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
};

// Read-shape: suggestion enriched with computed staleness plus the entity name/description
// as of the reviewed version. `isStale` is true when reviewed_entity_version_id is no
// longer the current version of its reviewed_entity_id (or the entity was deleted).
// Add-entity suggestions are never stale; their `reviewedEntity*` fields are null because
// no prior version exists.
export type AgenticSettingsEntityReviewSuggestionWithStaleness =
  AgenticSettingsEntityReviewSuggestion & {
    isStale: boolean;
    // The entity that applied_to_entity_version_id belongs to, so an accepted add_entity
    // (which has no reviewedEntityId) can still name the entity it created.
    appliedEntityId: string | null;
  };

export type AgenticSettingsEntityReviewWithSuggestions = {
  review: AgenticSettingsEntityReview;
  suggestions: AgenticSettingsEntityReviewSuggestionWithStaleness[];
};

// The save mutation raises this verbatim when expectedVersionId is stale; the
// Definitions tab matches on it to offer a reload instead of a generic failure.
export const AGENT_DEFINITION_STALE_VERSION_MESSAGE =
  "This definition changed since you loaded it. Reload to see the latest version.";

// The `reason` extension on the update mutation's error when expectedVersionId is no longer the latest version.
export const AGENTIC_SETTINGS_VERSION_CONFLICT_REASON =
  "AGENTIC_SETTINGS_VERSION_CONFLICT";
